import React, { useState } from 'react';
import { useOnboardingStore } from '../../store';
import { ArrowLeft, CheckCircle2, Info, FastForward, Users, Video, Camera, MapPin, Brain, Search, BarChart3 } from 'lucide-react';
import { canSkipSteps } from '../../config/beta';

interface IntegrationOption {
  id: string;
  name: string;
  description: string;
  required: boolean;
}

interface DisplayIntegrationOption extends IntegrationOption {
  serviceNames: string[];
}

const INTEGRATION_MAP: Record<string, IntegrationOption[]> = {
  'repeat-referral-system': [
    {
      id: 'crm-connection',
      name: 'CRM / Customer Database',
      description: 'Connect your customer management system to enable timing-based outreach',
      required: true
    },
    {
      id: 'email-platform',
      name: 'Email Platform',
      description: 'Connect your email system for automated customer communications',
      required: true
    },
    {
      id: 'sms-platform',
      name: 'SMS Platform (Optional)',
      description: 'Optional SMS integration for text-based outreach',
      required: false
    }
  ],
  'content-engine': [
    {
      id: 'website-access',
      name: 'Website / CMS Access',
      description: 'Grant access to publish articles to your website',
      required: true
    },
    {
      id: 'social-accounts',
      name: 'Social Media Accounts',
      description: 'Connect Facebook, Instagram, LinkedIn, etc. for content distribution',
      required: true
    },
    {
      id: 'google-business',
      name: 'Google Business Profile',
      description: 'Connect your Google Business Profile for local content publishing',
      required: false
    },
    {
      id: 'search-console',
      name: 'Google Search Console',
      description: 'Connect search performance data used for AI visibility, citation readiness, and reporting depth',
      required: false
    },
    {
      id: 'google-analytics',
      name: 'Google Analytics 4',
      description: 'Connect GA4 so reporting can use sessions, engagement, conversions, and organic traffic data',
      required: false
    }
  ],
  'proof-of-work': [
    {
      id: 'job-photo-tool',
      name: 'Job Photo Tool',
      description: 'Connect CompanyCam, Jobber, ServiceTitan, or other job photo system',
      required: true
    },
    {
      id: 'social-accounts',
      name: 'Social Media Accounts',
      description: 'Connect Facebook, Instagram, Google Business Profile for posting',
      required: true
    }
  ],
  'nap-entity-boost': [
    {
      id: 'canonical-business-info',
      name: 'Canonical Business Information',
      description: 'Use the business name, address, phone, and service area from this onboarding so listings are checked against one source of truth',
      required: true
    },
    {
      id: 'google-business',
      name: 'Google Business Profile',
      description: 'Connect or confirm the Google Business Profile that should be treated as the primary local profile',
      required: true
    },
    {
      id: 'website-access',
      name: 'Website / CMS Access',
      description: 'Confirm the website that should match your local listings and receive recommended NAP updates',
      required: false
    },
    {
      id: 'search-console',
      name: 'Google Search Console',
      description: 'Connect the property that matches your client website so audits can use real queries, pages, impressions, and clicks',
      required: false
    },
    {
      id: 'google-analytics',
      name: 'Google Analytics 4',
      description: 'Connect the GA4 property for website traffic, engagement, conversion, and organic performance metrics',
      required: false
    }
  ],
  'ai-audit': [
    {
      id: 'website-access',
      name: 'Website / Source Page Access',
      description: 'Confirm the website and priority pages that should be audited for AI visibility, source quality, crawl access, and citation readiness',
      required: true
    },
    {
      id: 'search-console',
      name: 'Google Search Console',
      description: 'Connect the property that matches your website so the audit can use real queries, pages, impressions, clicks, and AI visibility signals',
      required: true
    },
    {
      id: 'google-analytics',
      name: 'Google Analytics 4',
      description: 'Optional conversion and engagement data used to connect AI visibility work to visits and outcomes',
      required: false
    },
    {
      id: 'social-accounts',
      name: 'Social / Business Profiles',
      description: 'Optional profile connections used for richer entity, monitoring, and source consistency checks',
      required: false
    }
  ],
  'seo-gbp-audit': [
    {
      id: 'canonical-business-info',
      name: 'Canonical Business Information',
      description: 'Use the business name, address, phone, website, and service area as the source of truth for SEO and GBP checks',
      required: true
    },
    {
      id: 'google-business',
      name: 'Google Business Profile',
      description: 'Connect or confirm the Google Business Profile used for local visibility, profile readiness, and monthly change tracking',
      required: true
    },
    {
      id: 'search-console',
      name: 'Google Search Console',
      description: 'Connect the property that matches your website so the audit can use real organic search performance data',
      required: true
    },
    {
      id: 'google-analytics',
      name: 'Google Analytics 4',
      description: 'Optional traffic, engagement, and conversion data used to enrich the monthly SEO & GBP report',
      required: false
    }
  ],
  'customer-intelligence': [
    {
      id: 'crm-connection',
      name: 'CRM / Customer Database',
      description: 'Connect customer, job, referral, and revenue history so intelligence signals have a trusted source',
      required: true
    },
    {
      id: 'email-platform',
      name: 'Email Platform',
      description: 'Connect the customer communication system used by R&R for approval-gated follow-up',
      required: true
    },
    {
      id: 'sms-platform',
      name: 'SMS Platform (Optional)',
      description: 'Optional SMS connection for approved follow-up moments where text is appropriate',
      required: false
    },
    {
      id: 'google-analytics',
      name: 'Google Analytics 4',
      description: 'Optional conversion and attribution data used to measure signal performance',
      required: false
    }
  ]
};

function SocialPageAccessModal({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4" role="presentation" onClick={onClose}>
      <div
        className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl border border-[#22c55e]/50 bg-scale-darker p-6 text-left shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="social-page-access-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <h3 id="social-page-access-title" className="text-xl font-semibold text-white">
            Facebook and LinkedIn page access
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-600 px-3 py-2 text-sm font-semibold text-gray-200 hover:bg-white/10"
          >
            Close
          </button>
        </div>
        <p className="text-sm leading-6 text-gray-300">
          Use two logins in the right places. Your Scale Small AI login gets you into the dashboard or connect page.
          The Facebook or LinkedIn login in the popup must be the account that has admin access to the business page.
        </p>
        <div className="my-5 grid gap-3">
          {[
            ['1. Browser profile', 'Open an incognito window or Chrome profile where the Facebook or LinkedIn account with page admin access is signed in.'],
            ['2. Scale Small AI login', 'Go to dashboard.scalesmall.ai and sign in with the normal client dashboard login.'],
            ['3. Connect Platforms', 'Click Connect for Facebook or LinkedIn.'],
            ['4. Platform popup', 'When Facebook or LinkedIn opens, continue with the personal or admin account that manages the business/company page.'],
            ['5. Choose the page', 'If asked, select the business page or company page, approve access, and return to Scale Small AI.']
          ].map(([title, copy]) => (
            <div key={title} className="rounded-lg border border-[#22c55e]/30 border-l-4 border-l-[#22c55e] bg-[#22c55e]/10 p-4">
              <p className="font-semibold text-[#86efac]">{title}</p>
              <p className="mt-1 text-sm leading-5 text-[#d9f99d]">{copy}</p>
            </div>
          ))}
        </div>
        <p className="border-t border-gray-700 pt-4 text-sm leading-6 text-[#bbf7d0]">
          Important: the personal Facebook or LinkedIn account is not your Scale Small AI login. It is only used inside the platform popup so Facebook or LinkedIn can show the pages that account manages.
        </p>
      </div>
    </div>
  );
}

export function IntegrationSetup() {
  const { selectedServiceTypes, setStep, client } = useOnboardingStore();
  const [acknowledgedIntegrations, setAcknowledgedIntegrations] = useState<Set<string>>(new Set());
  const [showPageAccessInfo, setShowPageAccessInfo] = useState(false);

  const selectedServiceNames = selectedServiceTypes.map((service) => service.name);
  const integrations = selectedServiceTypes.reduce<DisplayIntegrationOption[]>((acc, service) => {
    const serviceIntegrations = INTEGRATION_MAP[service.id] || [];
    serviceIntegrations.forEach((integration) => {
      const existing = acc.find((item) => item.id === integration.id);
      if (existing) {
        existing.required = existing.required || integration.required;
        if (!existing.serviceNames.includes(service.name)) {
          existing.serviceNames.push(service.name);
        }
        return;
      }

      acc.push({
        ...integration,
        serviceNames: [service.name],
      });
    });
    return acc;
  }, []);

  const handleToggleIntegration = (integrationId: string) => {
    setAcknowledgedIntegrations(prev => {
      const newSet = new Set(prev);
      if (newSet.has(integrationId)) {
        newSet.delete(integrationId);
      } else {
        newSet.add(integrationId);
      }
      return newSet;
    });
  };

  const handleSkip = () => {
    if (canSkipSteps()) {
      setStep(6);
    }
  };

  const handleNext = () => {
    setStep(6);
  };

  const requiredIntegrations = integrations.filter(i => i.required);
  const allRequiredAcknowledged = requiredIntegrations.every(i => acknowledgedIntegrations.has(i.id));

  const getServiceIcon = () => {
    if (selectedServiceTypes.length !== 1) return <Info className="w-8 h-8 text-scale-blue" />;

    switch (selectedServiceTypes[0]?.id) {
      case 'repeat-referral-system':
        return <Users className="w-8 h-8 text-scale-blue" />;
      case 'content-engine':
        return <Video className="w-8 h-8 text-scale-blue" />;
      case 'proof-of-work':
        return <Camera className="w-8 h-8 text-scale-blue" />;
      case 'nap-entity-boost':
        return <MapPin className="w-8 h-8 text-scale-blue" />;
      case 'ai-audit':
        return <Search className="w-8 h-8 text-scale-blue" />;
      case 'seo-gbp-audit':
        return <BarChart3 className="w-8 h-8 text-scale-blue" />;
      case 'customer-intelligence':
        return <Brain className="w-8 h-8 text-scale-blue" />;
      default:
        return null;
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      {showPageAccessInfo && <SocialPageAccessModal onClose={() => setShowPageAccessInfo(false)} />}
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-scale-blue/10 rounded-full flex items-center justify-center mx-auto mb-4">
          {getServiceIcon()}
        </div>
        <h2 className="text-2xl font-semibold text-white mb-2">Integration Requirements</h2>
        <p className="text-gray-400">
          {selectedServiceNames.length > 0
            ? `Required connections for ${selectedServiceNames.join(', ')}`
            : 'Loading...'}
        </p>
      </div>

      <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-400 mb-2">What this step means</h3>
            <p className="text-gray-300 text-sm">
              Review what your selected services will need. We reuse the business and contact details already entered, then Connect asks only for platform approvals we cannot safely do for you.
            </p>
            {(client.businessName || client.address || client.phone) && (
              <div className="mt-3 rounded border border-blue-400/20 bg-black/20 p-3 text-xs leading-5 text-blue-100">
                <strong className="text-blue-300">Already carried forward:</strong>{' '}
                {[client.businessName, client.address, client.phone].filter(Boolean).join(' • ')}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-4 mb-8">
        {integrations.map((integration) => {
          const isAcknowledged = acknowledgedIntegrations.has(integration.id);

          // Get integration-specific instructions
          const getIntegrationInstructions = () => {
            switch (integration.id) {
              case 'website-access':
                return (
                  <div className="mt-3 p-3 bg-blue-500/5 border border-blue-500/20 rounded text-xs text-gray-300">
                    <strong className="text-blue-400">Setup Required:</strong> Your website will need a safe way to receive articles or NAP updates, usually through your CMS, hosting login, or a webhook.
                    You can connect this later in the dashboard setup flow; we will not ask for the business details again.
                  </div>
                );
              case 'social-accounts':
                return (
                  <div className="mt-3 space-y-2">
                    <div className="p-3 bg-blue-500/5 border border-blue-500/20 rounded text-xs text-gray-300">
                      <strong className="text-blue-400">Easy Setup:</strong> Your completion email includes your login and a link to Connect.
                      Use Connect to turn on only the social platforms you want and authorize the accounts that should publish.
                    </div>
                    <div className="p-3 bg-[#22c55e]/10 border border-[#22c55e]/40 rounded text-xs text-[#bbf7d0]">
                      <strong className="text-[#22c55e]">Facebook & LinkedIn page access:</strong> Open <strong>dashboard.scalesmall.ai</strong> in an incognito window or Chrome profile where the page admin account is signed in. Then sign in to Scale Small AI with the normal dashboard login, click Connect, and use the Facebook or LinkedIn popup with the account that manages the business/company page.
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          setShowPageAccessInfo(true);
                        }}
                        className="mt-2 block font-black underline underline-offset-4 text-[#22c55e]"
                      >
                        Click here for more info
                      </button>
                    </div>
                  </div>
                );
              case 'google-business':
                return (
                  <div className="mt-3 p-3 bg-blue-500/5 border border-blue-500/20 rounded text-xs text-gray-300">
                    <strong className="text-blue-400">Simple Approval:</strong> If you use Google Business Profile, connect it from Connect or the dashboard with the Google account that manages the business profile.
                  </div>
                );
              case 'search-console':
                return (
                  <div className="mt-3 p-3 bg-blue-500/5 border border-blue-500/20 rounded text-xs text-gray-300">
                    <strong className="text-blue-400">OAuth Setup:</strong> Connect Google Search Console from Connect or the dashboard with the Google account that owns or manages the website property.
                    This gives Scale Small AI read-only search performance access for AI Audit, citation readiness, and SEO & GBP Audit.
                  </div>
                );
              case 'google-analytics':
                return (
                  <div className="mt-3 p-3 bg-blue-500/5 border border-blue-500/20 rounded text-xs text-gray-300">
                    <strong className="text-blue-400">OAuth Setup:</strong> Connect Google Analytics 4 from Connect or the dashboard, then choose the exact GA4 property for this business.
                  </div>
                );
              case 'canonical-business-info':
                return (
                  <div className="mt-3 p-3 bg-[#22c55e]/10 border border-[#22c55e]/30 rounded text-xs text-[#bbf7d0]">
                    <strong className="text-[#22c55e]">No re-entry:</strong> The business name, phone, and address from the first step become the default source of truth for listing checks and dashboard review.
                  </div>
                );
              default:
                return null;
            }
          };

          return (
            <div
              key={integration.id}
              onClick={() => handleToggleIntegration(integration.id)}
              className={`cursor-pointer rounded-lg p-6 transition-all duration-200 border ${
                isAcknowledged
                  ? 'bg-scale-blue/5 border-scale-blue'
                  : 'bg-scale-darker border-gray-700 hover:border-gray-600'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                  isAcknowledged
                    ? 'border-scale-blue bg-scale-blue'
                    : 'border-gray-600'
                }`}>
                  {isAcknowledged && (
                    <CheckCircle2 className="w-4 h-4 text-scale-dark" />
                  )}
                </div>

                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-medium text-white">{integration.name}</h3>
                    {integration.required && (
                      <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded">
                        Required
                      </span>
                    )}
                  </div>
                  <p className="text-gray-400 text-sm">{integration.description}</p>
                  {integration.serviceNames.length > 0 && (
                    <p className="mt-2 text-xs text-gray-500">
                      Used for: <span className="text-gray-300">{integration.serviceNames.join(', ')}</span>
                    </p>
                  )}
                  {getIntegrationInstructions()}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex justify-between space-x-4 pt-4">
        <button
          onClick={() => setStep(4)}
          className="flex items-center px-6 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </button>
        <div className="flex gap-3">
          {canSkipSteps() && (
            <button
              onClick={handleSkip}
              className="flex items-center bg-yellow-600 text-white px-4 py-2 rounded-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 transition-colors font-medium"
            >
              <FastForward className="w-4 h-4 mr-2" />
              Skip (Beta)
            </button>
          )}
          <button
            onClick={handleNext}
            disabled={!allRequiredAcknowledged}
            className={`flex items-center px-6 py-2 rounded-md transition-colors ${
              allRequiredAcknowledged
                ? 'bg-scale-blue text-scale-dark hover:bg-white'
                : 'bg-gray-600 text-gray-400 cursor-not-allowed'
            }`}
          >
            Continue to Final Submit
          </button>
        </div>
      </div>
    </div>
  );
}
